import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  CloudUpload,
  GraduationCap,
  Plus,
  Rocket,
  Trash2,
  User,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CANDIDATE_PROFILE_UPDATED_EVENT } from './CandidateOnboardingModal';
import { trackerToast } from '../HV/Tracker/trackerToast.js';
import { Button, Checkbox, Input, Progress } from '@/components/ui';
import { toMonthInputValue, clampMonthYearValue } from '../utils/experienceDates';

const STEPS = ['welcome', 'basic', 'experience', 'education'] as const;
type Step = (typeof STEPS)[number];

interface ExperienceEntry {
  designation: string;
  organization: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string;
}

interface EducationEntry {
  degree: string;
  institution: string;
  startDate: string;
  endDate: string;
}

interface CandidateData {
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  currentLocation?: string;
  currentCompany?: string;
  currentRole?: string;
  experience_yrs?: number;
  noticePeriod?: number;
  compensation?: number;
  expectation?: string;
  resume?: string;
  experience?: ExperienceEntry[];
  education?: EducationEntry[];
}

const emptyExperience = (): ExperienceEntry => ({
  designation: '',
  organization: '',
  location: '',
  startDate: '',
  endDate: '',
  description: '',
});

const emptyEducation = (): EducationEntry => ({
  degree: '',
  institution: '',
  startDate: '',
  endDate: '',
});

const STEP_LABELS: Record<'basic' | 'experience' | 'education', string> = {
  basic: 'Basic details',
  experience: 'Work experience',
  education: 'Education',
};

type BasicForm = {
  name: string;
  experience_yrs: string;
  noticePeriod: string;
  compensation: string;
  expectation: string;
  phone: string;
  linkedin: string;
  currentLocation: string;
  resume: string;
  resumeFileName: string;
};

const INITIAL_BASIC: BasicForm = {
  name: '',
  experience_yrs: '',
  noticePeriod: '',
  compensation: '',
  expectation: '',
  phone: '',
  linkedin: '',
  currentLocation: '',
  resume: '',
  resumeFileName: '',
};

interface OnboardingDraft {
  step: Step;
  basic: BasicForm;
  experiences: ExperienceEntry[];
  education: EducationEntry[];
  educationSkipped: boolean;
  experienceSkipped: boolean;
  updatedAt: string;
}

function getDraftStorageKey(email: string) {
  return `candidate-onboarding:${email.trim().toLowerCase()}`;
}

function readOnboardingDraft(email: string): OnboardingDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getDraftStorageKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardingDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!STEPS.includes(parsed.step)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeOnboardingDraft(email: string, draft: OnboardingDraft) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getDraftStorageKey(email), JSON.stringify(draft));
  } catch (err) {
    console.error('Failed to save onboarding draft', err);
  }
}

function clearOnboardingDraft(email: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(getDraftStorageKey(email));
  } catch (err) {
    console.error('Failed to clear onboarding draft', err);
  }
}

function applyDraft(draft: OnboardingDraft) {
  return {
    step: draft.step,
    basic: { ...INITIAL_BASIC, ...draft.basic },
    experiences: draft.experiences?.length ? draft.experiences : [emptyExperience()],
    education: draft.education?.length ? draft.education : [emptyEducation()],
    educationSkipped: !!draft.educationSkipped,
    experienceSkipped: !!draft.experienceSkipped,
  };
}

function candidateToBasic(data: CandidateData, fallbackName: string): BasicForm {
  return {
    name: data.name || fallbackName,
    experience_yrs: data.experience_yrs != null ? String(data.experience_yrs) : '',
    noticePeriod: data.noticePeriod != null ? String(data.noticePeriod) : '',
    compensation: stripLakh(data.compensation),
    expectation: stripLakh(data.expectation),
    phone: data.phone || '',
    linkedin: data.linkedin || '',
    currentLocation: data.currentLocation || '',
    resume: data.resume || '',
    resumeFileName: data.resume ? data.resume.split('/').pop() || 'Resume uploaded' : '',
  };
}

function candidateToExperiences(data: CandidateData): ExperienceEntry[] {
  if (!Array.isArray(data.experience) || data.experience.length === 0) {
    return [emptyExperience()];
  }
  return data.experience.map((exp) => ({
    designation: exp.designation || '',
    organization: exp.organization || '',
    location: exp.location || '',
    startDate: exp.startDate || '',
    endDate: exp.endDate || '',
    description: exp.description || '',
  }));
}

function candidateToEducation(data: CandidateData): EducationEntry[] {
  if (!Array.isArray(data.education) || data.education.length === 0) {
    return [emptyEducation()];
  }
  return data.education.map((edu) => ({
    degree: edu.degree || '',
    institution: edu.institution || '',
    startDate: edu.startDate || '',
    endDate: edu.endDate || '',
  }));
}

function cleanExperiences(experiences: ExperienceEntry[]) {
  return experiences
    .map((exp) => ({
      designation: exp.designation.trim(),
      organization: exp.organization.trim(),
      location: exp.location.trim(),
      startDate: exp.startDate.trim(),
      endDate: exp.endDate.trim(),
      description: exp.description.trim(),
    }))
    .filter((exp) => exp.organization || exp.designation);
}

function cleanEducationEntries(education: EducationEntry[]) {
  return education
    .map((edu) => ({
      degree: edu.degree.trim(),
      institution: edu.institution.trim(),
      startDate: edu.startDate.trim(),
      endDate: edu.endDate.trim(),
    }))
    .filter((edu) => edu.degree || edu.institution);
}

function buildPartialPayload(
  basic: BasicForm,
  experiences: ExperienceEntry[],
  education: EducationEntry[],
  educationSkipped: boolean,
  experienceSkipped: boolean,
  email?: string,
): Partial<CandidateData> {
  const payload: Partial<CandidateData> = {};
  if (email) payload.email = email;
  if (basic.name.trim()) payload.name = basic.name.trim();
  if (basic.phone.trim()) payload.phone = basic.phone.trim();
  if (basic.linkedin.trim()) payload.linkedin = basic.linkedin.trim();
  if (basic.currentLocation.trim()) payload.currentLocation = basic.currentLocation.trim();
  if (basic.experience_yrs.trim()) payload.experience_yrs = Number(basic.experience_yrs);
  if (basic.noticePeriod.trim()) payload.noticePeriod = Number(basic.noticePeriod);
  if (basic.compensation.trim()) payload.compensation = Number(basic.compensation);
  if (basic.expectation.trim()) {
    payload.expectation =
      basic.expectation.trim().endsWith('L') || basic.expectation.trim().endsWith('l')
        ? basic.expectation.trim()
        : `${basic.expectation.trim()}L`;
  }
  if (basic.resume.trim()) payload.resume = basic.resume.trim();

  if (!experienceSkipped) {
    const exp = cleanExperiences(experiences);
    if (exp.length) {
      payload.experience = exp;
      payload.currentCompany = exp[0].organization;
      payload.currentRole = exp[0].designation;
    }
  }

  if (!educationSkipped) {
    const edu = cleanEducationEntries(education);
    if (edu.length) payload.education = edu;
  }

  return payload;
}

function stripLakh(v: unknown) {
  if (v === undefined || v === null || v === '') return '';
  return String(v).replace(/\s*l\s*$/i, '').trim();
}

interface CandidateOnboardingFlowProps {
  onClose?: () => void;
}

export function CandidateOnboardingFlow({ onClose }: CandidateOnboardingFlowProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('welcome');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [uploadingResume, setUploadingResume] = useState(false);
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [educationSkipped, setEducationSkipped] = useState(false);
  const [experienceSkipped, setExperienceSkipped] = useState(false);
  const skipAutosaveRef = useRef(true);

  const [basic, setBasic] = useState<BasicForm>(INITIAL_BASIC);

  const [experiences, setExperiences] = useState<ExperienceEntry[]>([emptyExperience()]);
  const [education, setEducation] = useState<EducationEntry[]>([emptyEducation()]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user?.email) return;

    const load = async () => {
      let serverData: CandidateData | null = null;
      let id: string | null = null;

      try {
        const { data } = await axios.get<CandidateData>(`/api/candidates/email/${user.email}`);
        serverData = data;
        id = data._id ?? null;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          try {
            const { data } = await axios.post<CandidateData>('/api/candidates', {
              name: user.name || '',
              email: user.email,
              CreatedBy: user.email,
            });
            serverData = data;
            id = data._id ?? null;
          } catch (createErr) {
            console.error('Failed to create candidate profile', createErr);
          }
        } else {
          console.error('Failed to load candidate profile', err);
        }
      }

      setCandidateId(id);

      const draft = readOnboardingDraft(user.email);
      if (draft) {
        const restored = applyDraft(draft);
        setStep(restored.step);
        setBasic(restored.basic);
        setExperiences(restored.experiences);
        setEducation(restored.education);
        setEducationSkipped(restored.educationSkipped);
        setExperienceSkipped(restored.experienceSkipped);
      } else if (serverData) {
        setBasic(candidateToBasic(serverData, user.name || ''));
        setExperiences(candidateToExperiences(serverData));
        setEducation(candidateToEducation(serverData));
      } else {
        setBasic((prev) => ({ ...prev, name: user.name || '' }));
      }

      skipAutosaveRef.current = false;
      setLoading(false);
    };

    load();
  }, [user]);

  useEffect(() => {
    if (loading || !user?.email || skipAutosaveRef.current) return;

    setAutosaveStatus('saving');
    const localTimer = window.setTimeout(() => {
      writeOnboardingDraft(user.email, {
        step,
        basic,
        experiences,
        education,
        educationSkipped,
        experienceSkipped,
        updatedAt: new Date().toISOString(),
      });
      setAutosaveStatus('saved');
    }, 400);

    const serverTimer = window.setTimeout(async () => {
      if (!candidateId) return;
      try {
        const payload = buildPartialPayload(
          basic,
          experiences,
          education,
          educationSkipped,
          experienceSkipped,
          user.email,
        );
        if (Object.keys(payload).length > 1) {
          await axios.put(`/api/candidates/${candidateId}`, payload);
        }
      } catch (err) {
        console.error('Onboarding autosave failed', err);
        setAutosaveStatus('error');
      }
    }, 1200);

    return () => {
      window.clearTimeout(localTimer);
      window.clearTimeout(serverTimer);
    };
  }, [
    loading,
    user?.email,
    step,
    basic,
    experiences,
    education,
    educationSkipped,
    experienceSkipped,
    candidateId,
  ]);

  const stepIndex = STEPS.indexOf(step);
  const formStepCount = STEPS.length - 1;
  const progressValue =
    step === 'welcome' ? 5
      : (stepIndex / formStepCount) * 100;

  const setBasicField = (field: keyof typeof basic, value: string) => {
    setBasic((prev) => ({ ...prev, [field]: value }));
  };

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingResume(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.resume;
      return next;
    });

    try {
      const formData = new FormData();
      formData.append('resume', file);
      const { data } = await axios.post<{ filePath: string }>('/api/upload/resume', formData);
      setBasic((prev) => ({
        ...prev,
        resume: data.filePath,
        resumeFileName: file.name,
      }));
    } catch (err) {
      console.error('Resume upload failed', err);
      setErrors((prev) => ({ ...prev, resume: 'Failed to upload resume. Try again.' }));
    } finally {
      setUploadingResume(false);
      if (event.target) event.target.value = '';
    }
  };

  const updateExperience = (index: number, field: keyof ExperienceEntry, value: string) => {
    setExperiences((prev) =>
      prev.map((exp, i) => (i === index ? { ...exp, [field]: value } : exp)),
    );
  };

  const toggleExperiencePresent = (index: number, checked: boolean) => {
    updateExperience(index, 'endDate', checked ? 'Present' : '');
  };

  const addExperience = () => {
    setExperiences((prev) => [...prev, emptyExperience()]);
  };

  const removeExperience = (index: number) => {
    setExperiences((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const updateEducation = (index: number, field: keyof EducationEntry, value: string) => {
    setEducation((prev) =>
      prev.map((edu, i) => (i === index ? { ...edu, [field]: value } : edu)),
    );
  };

  const addEducation = () => {
    setEducation((prev) => [...prev, emptyEducation()]);
  };

  const removeEducation = (index: number) => {
    setEducation((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const saveProfile = async () => {
    if (!candidateId) throw new Error('No candidate profile to save');
    const payload = buildPartialPayload(
      basic,
      experiences,
      education,
      educationSkipped,
      experienceSkipped,
      user?.email,
    );
    if (Object.keys(payload).length <= 1) return;
    await axios.put(`/api/candidates/${candidateId}`, payload);
    window.dispatchEvent(new Event(CANDIDATE_PROFILE_UPDATED_EVENT));
  };

  const finishOnboarding = async () => {
    setSaving(true);
    try {
      await saveProfile();
      if (user?.email) clearOnboardingDraft(user.email);
      trackerToast.success('Profile data saved successfully');
      onClose?.();
    } catch (err) {
      console.error('Failed to complete onboarding', err);
      trackerToast.error('Something went wrong. Please try again.');
      setErrors({ form: 'Something went wrong. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (step === 'welcome') {
      setStep('basic');
      return;
    }
    if (step === 'basic') {
      setStep('experience');
      return;
    }
    if (step === 'experience') {
      setStep('education');
      return;
    }
    if (step === 'education') {
      setEducationSkipped(false);
      finishOnboarding();
    }
  };

  const handleSkipExperience = () => {
    setExperienceSkipped(true);
    setExperiences([emptyExperience()]);
    setStep('education');
  };

  const handleBack = () => {
    if (step === 'basic') setStep('welcome');
    else if (step === 'experience') setStep('basic');
    else if (step === 'education') setStep('experience');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-[var(--text-muted)]">Setting up your profile…</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mx-auto max-w-2xl">
        {step !== 'welcome' && (
          <div className="mb-8">
            <div className="mb-3 flex items-center justify-between text-xs text-[var(--text-muted)]">
              <span>{`Step ${stepIndex} of ${formStepCount}`}</span>
              <span className="flex items-center gap-2">
                {autosaveStatus === 'saving' && 'Saving…'}
                {autosaveStatus === 'saved' && 'Draft saved'}
                {autosaveStatus === 'error' && 'Save failed'}
                <span>{Math.round(progressValue)}%</span>
              </span>
            </div>
            <Progress value={progressValue} className="h-1.5" />
          </div>
        )}

        <div className="rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-6 shadow-lg sm:p-8">
          {step === 'welcome' && (
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[color-mix(in_srgb,var(--primary)_15%,transparent)]">
                <Rocket className="h-8 w-8 text-[var(--primary)]" />
              </div>
              <h1
                className="mb-3 text-3xl font-bold text-[var(--text-primary)]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Welcome to HireVeda
              </h1>
              <p className="mb-8 leading-relaxed text-[var(--text-secondary)]">
                {user?.name ? `Hi ${user.name.split(' ')[0]}, ` : ''}
                let&apos;s set up your profile so recruiters can discover you and match you with the right opportunities.
              </p>
              <div className="mb-8 space-y-4 text-left">
                {[
                  { icon: <User size={18} />, title: 'Build your profile', desc: 'Share your basics so we can represent you well' },
                  { icon: <Briefcase size={18} />, title: 'Highlight your experience', desc: 'Add your current role and company' },
                  { icon: <GraduationCap size={18} />, title: 'Stand out to recruiters', desc: 'A complete profile gets more visibility' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3 rounded-xl border border-[var(--border-default)] bg-[var(--bg-subtle)] p-4">
                    <div className="mt-0.5 text-[var(--primary)]">{item.icon}</div>
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">{item.title}</p>
                      <p className="text-sm text-[var(--text-muted)]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col items-center gap-3">
                <Button onClick={handleNext} className="w-full gap-2 sm:w-auto" size="lg">
                  Get started
                  <ArrowRight size={16} />
                </Button>
               
              </div>
              <p className="mt-4 text-xs text-[var(--text-muted)]">Takes about 2 minutes</p>
            </div>
          )}

          {step === 'basic' && (
            <div>
              <StepHeader icon={<User size={20} />} title={STEP_LABELS.basic} subtitle="Your core details" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" error={errors.name} className="sm:col-span-2">
                  <Input value={basic.name} onChange={(e) => setBasicField('name', e.target.value)} placeholder="Your full name" />
                </Field>
                <Field label="Email" className="sm:col-span-2">
                  <Input value={user?.email || ''} readOnly disabled className="cursor-not-allowed opacity-70" />
                </Field>
                <Field label="Years of experience" error={errors.experience_yrs}>
                  <Input value={basic.experience_yrs} onChange={(e) => setBasicField('experience_yrs', e.target.value)} placeholder="e.g. 5" type="number" min={0} />
                </Field>
                <Field label="Notice period (days)" error={errors.noticePeriod}>
                  <Input value={basic.noticePeriod} onChange={(e) => setBasicField('noticePeriod', e.target.value)} placeholder="e.g. 30, 60, 90" type="number" min={0} />
                </Field>
                <Field label="Current CTC (Lakhs)" error={errors.compensation}>
                  <Input value={basic.compensation} onChange={(e) => setBasicField('compensation', e.target.value)} placeholder="e.g. 25" type="number" min={0} />
                </Field>
                <Field label="Expected CTC (Lakhs)" error={errors.expectation}>
                  <Input value={basic.expectation} onChange={(e) => setBasicField('expectation', e.target.value)} placeholder="e.g. 35" type="number" min={0} />
                </Field>
                <Field label="Phone" error={errors.phone}>
                  <Input value={basic.phone} onChange={(e) => setBasicField('phone', e.target.value)} placeholder="+91 98765 43210" type="tel" />
                </Field>
                <Field label="LinkedIn" error={errors.linkedin}>
                  <Input value={basic.linkedin} onChange={(e) => setBasicField('linkedin', e.target.value)} placeholder="linkedin.com/in/you" />
                </Field>
                <Field label="Location" error={errors.currentLocation} className="sm:col-span-2">
                  <Input value={basic.currentLocation} onChange={(e) => setBasicField('currentLocation', e.target.value)} placeholder="e.g. Bangalore, India" />
                </Field>
                <Field label="Resume" error={errors.resume} className="sm:col-span-2">
                  <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleResumeUpload} />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingResume}
                    className="flex w-full items-center gap-3 rounded-xl border border-dashed border-[var(--border-default)] bg-[var(--bg-subtle)] px-4 py-4 text-left transition-colors hover:border-[var(--primary)] hover:bg-[color-mix(in_srgb,var(--primary)_6%,transparent)]"
                  >
                    <CloudUpload className="h-5 w-5 shrink-0 text-[var(--primary)]" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">
                        {uploadingResume ? 'Uploading…' : basic.resumeFileName || 'Upload resume (PDF, DOC)'}
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {basic.resume ? 'Click to replace' : 'Optional — PDF or DOC'}
                      </p>
                    </div>
                  </button>
                </Field>
              </div>
              <NavButtons onBack={handleBack} onNext={handleNext} />
            </div>
          )}

          {step === 'experience' && (
            <div>
              <StepHeader icon={<Briefcase size={20} />} title={STEP_LABELS.experience} subtitle="Add all your work experience" />
              <div className="space-y-5">
                {experiences.map((exp, index) => (
                  <div key={index} className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-subtle)] p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-[var(--text-primary)]">
                        Experience {index + 1}
                        {index === 0 && <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(most recent)</span>}
                      </p>
                      {experiences.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeExperience(index)} className="h-8 text-[var(--text-danger)]">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Company">
                        <Input value={exp.organization} onChange={(e) => updateExperience(index, 'organization', e.target.value)} placeholder="Company name" />
                      </Field>
                      <Field label="Designation">
                        <Input value={exp.designation} onChange={(e) => updateExperience(index, 'designation', e.target.value)} placeholder="Your role" />
                      </Field>
                      <Field label="Location">
                        <Input value={exp.location} onChange={(e) => updateExperience(index, 'location', e.target.value)} placeholder="City" />
                      </Field>
                      <Field label="Start date">
                        <MonthInput
                          value={toMonthInputValue(exp.startDate)}
                          onChange={(value) => updateExperience(index, 'startDate', value)}
                        />
                      </Field>
                      <div>
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <label className="text-sm font-medium text-[var(--text-primary)]">End date</label>
                          <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text-secondary)]">
                            <Checkbox
                              checked={exp.endDate === 'Present'}
                              onCheckedChange={(checked) => toggleExperiencePresent(index, checked === true)}
                            />
                            Present
                          </label>
                        </div>
                        <MonthInput
                          value={exp.endDate === 'Present' ? '' : toMonthInputValue(exp.endDate)}
                          disabled={exp.endDate === 'Present'}
                          onChange={(value) => updateExperience(index, 'endDate', value)}
                        />
                      </div>
                      <Field label="Description" className="sm:col-span-2">
                        <textarea
                          value={exp.description}
                          onChange={(e) => updateExperience(index, 'description', e.target.value)}
                          placeholder="Key responsibilities and achievements"
                          rows={3}
                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addExperience} className="mt-4 w-full gap-2">
                <Plus size={16} />
                Add more experience
              </Button>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button variant="ghost" onClick={handleBack}>Back</Button>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button variant="outline" onClick={handleSkipExperience}>Skip experience</Button>
                  <Button onClick={handleNext} className="gap-2">
                    Continue
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 'education' && (
            <div>
              <StepHeader icon={<GraduationCap size={20} />} title={STEP_LABELS.education} subtitle="Add your education (optional)" />
              <div className="space-y-5">
                {education.map((edu, index) => (
                  <div key={index} className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-subtle)] p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-[var(--text-primary)]">Education {index + 1}</p>
                      {education.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeEducation(index)} className="h-8 text-[var(--text-danger)]">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Degree / qualification">
                        <Input value={edu.degree} onChange={(e) => updateEducation(index, 'degree', e.target.value)} placeholder="e.g. B.Tech, MBA" />
                      </Field>
                      <Field label="Institution">
                        <Input value={edu.institution} onChange={(e) => updateEducation(index, 'institution', e.target.value)} placeholder="College / university" />
                      </Field>
                      <Field label="Start year">
                        <Input value={edu.startDate} onChange={(e) => updateEducation(index, 'startDate', e.target.value)} placeholder="e.g. 2016" />
                      </Field>
                      <Field label="End year">
                        <Input value={edu.endDate} onChange={(e) => updateEducation(index, 'endDate', e.target.value)} placeholder="e.g. 2020" />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addEducation} className="mt-4 w-full gap-2">
                <Plus size={16} />
                Add more education
              </Button>
              {errors.form && (
                <p className="mb-4 rounded-lg border border-[var(--error-border)] bg-[var(--error-light)] px-3 py-2 text-sm text-[var(--text-danger)]">
                  {errors.form}
                </p>
              )}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button variant="ghost" onClick={handleBack} disabled={saving}>Back</Button>
                <Button onClick={handleNext} disabled={saving} className="gap-2">
                  {saving ? 'Submitting…' : 'Submit'}
                  {!saving && <CheckCircle2 size={16} />}
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-[var(--text-muted)]">
          Your data is secure and only shared with recruiters when you apply.
        </p>
      </div>
    </div>
  );
}

export default CandidateOnboardingFlow;

function StepHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="mb-6 flex gap-3 rounded-xl border border-[var(--border-default)] bg-[var(--bg-subtle)] p-4">
      <div className="mt-0.5 shrink-0 text-[var(--primary)]">{icon}</div>
      <div>
        <h2 className="font-medium text-[var(--text-primary)]">{title}</h2>
        <p className="text-sm text-[var(--text-muted)]">{subtitle}</p>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
  className = '',
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
        {label}
        {required && <span className="text-[var(--text-danger)]"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-[var(--text-danger)]">{error}</p>}
    </div>
  );
}

function NavButtons({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      <Button variant="ghost" onClick={onBack}>Back</Button>
      <Button onClick={onNext} className="gap-2">
        Continue
        <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function MonthInput({
  value,
  onChange,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <input
      type="month"
      min="1900-01"
      max="2100-12"
      value={toMonthInputValue(value) || clampMonthYearValue(value)}
      disabled={disabled}
      onChange={(e) => onChange(clampMonthYearValue(e.target.value))}
      onInput={(e) => onChange(clampMonthYearValue(e.currentTarget.value))}
      className={[
        'flex h-10 w-full min-w-0 rounded-md border border-[var(--border-default)]',
        'bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-surface)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
        '[color-scheme:light] dark:[color-scheme:dark]',
        '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
        '[&::-webkit-calendar-picker-indicator]:opacity-100',
        '[&::-webkit-calendar-picker-indicator]:ml-1',
        'dark:[&::-webkit-calendar-picker-indicator]:invert',
      ].join(' ')}
    />
  );
}

